import type { ReactNode } from "react";

import { useDispatch, useSelector, useStore } from "react-redux";

import FilterPanelContainer from "app/pages/sales-intelligence/modules/advanced-search-filtering/containers/FilterPanel/FilterPanelContainer";
import { useFiltersConfigContext } from "app/pages/sales-intelligence/modules/advanced-search-filtering/context/FiltersConfigContext";
import { filtersConfigStatusMap } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filtersConfig";
import type { IFilterManagerModel } from "app/pages/sales-intelligence/modules/advanced-search-filtering/services/filters-manager/types";
import { clearThreadContext } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/store";
import {
    type LeadsSearchFilterKey,
    type LeadsSearchFilterValue,
    selectFilterMode,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { selectActivePageTab } from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import type { RootState } from "single-spa/store/types";

import { FiltersChatContainer } from "../../containers/FiltersChat";
import useFiltersValueState from "../../hooks/useFiltersValueState";
import { useInitialFilterCategoryFromRoute } from "../../hooks/useInitialFilterCategoryFromRoute";
import { filterTrackingRegistry } from "../../services/tracking/formatters";
import leadsSearchFiltersTrackingService from "../../services/tracking/trackingService";
import { selectSearchQuery } from "../../store/selectors";
import type { LeadsSearchFilterInstance } from "../../types/leadsSearchFilterInstance";
import type { LeadsSearchAllFiltersTypes } from "../../types/leadsSearchFilters.dto";

const DEFAULT_CONFIG: readonly LeadsSearchFilterInstance[] = [];

type Props = {
    isLoading: boolean;
    withAiAgentFilterMode?: boolean;
    belowSearchSlot?: ReactNode;
    onClearAllFilters?: () => void;
};

export const LeadsSearchFiltersContainer = ({
    isLoading,
    withAiAgentFilterMode = false,
    belowSearchSlot,
    onClearAllFilters,
}: Props) => {
    const dispatch = useDispatch();

    const activeTab = useSelector(selectActivePageTab);
    const filterMode = useSelector(selectFilterMode);
    const store = useStore<RootState>();

    useInitialFilterCategoryFromRoute();

    const filtersConfig = useFiltersConfigContext<LeadsSearchFilterInstance>();

    const [filterPanelValue, handleFiltersUpdate] = useFiltersValueState();

    const handleTrackFilterUpdate = (key: LeadsSearchFilterKey, value?: LeadsSearchFilterValue) => {
        const valueForTracking = filterTrackingRegistry.format(key, value);

        const currentQuery = selectSearchQuery(store.getState());
        if (currentQuery.length > 0 && !is.undefined(value)) {
            leadsSearchFiltersTrackingService.trackSearchFilterApply(key, currentQuery);
        }

        leadsSearchFiltersTrackingService.trackFilterValueChange(key, activeTab ?? "", valueForTracking);
    };

    const handleTrackClearFilter = (key: string) => {
        leadsSearchFiltersTrackingService.trackFilterValueClear(key, activeTab ?? "");
    };

    const handleClearAllFilters = (): void => {
        if (filterMode === "ai-agent") {
            void dispatch(clearThreadContext());
        }

        onClearAllFilters?.();

        leadsSearchFiltersTrackingService.trackFilterClearAll();
    };

    const renderChatSlot = (filtersManager: IFilterManagerModel<LeadsSearchAllFiltersTypes>) => (
        <FiltersChatContainer filtersManager={filtersManager} />
    );

    return (
        <FilterPanelContainer<LeadsSearchAllFiltersTypes, LeadsSearchFilterInstance>
            isLoading={isLoading}
            filtersConfig={
                filtersConfig.status === filtersConfigStatusMap.pending ? DEFAULT_CONFIG : filtersConfig.filtersList
            }
            filtersValue={filterPanelValue}
            activeTab={activeTab}
            withAiAgentFilterMode={withAiAgentFilterMode}
            renderChatSlot={renderChatSlot}
            belowSearchSlot={belowSearchSlot}
            onFiltersUpdate={handleFiltersUpdate}
            onFilterUpdate={handleTrackFilterUpdate}
            onClearFilter={handleTrackClearFilter}
            onClearAllFilters={handleClearAllFilters}
        />
    );
};
